import { Controller } from "@hotwired/stimulus"

// Passkeys (WebAuthn): `register` enrols this device for the signed-in user;
// `signIn` authenticates with a discoverable credential; `conditional` offers
// passkeys in the browser's autofill on the login field. Unsupported browsers
// simply never see the buttons.
export default class extends Controller {
  static targets = ["button", "message", "nickname"]
  static values = {
    creationOptionsUrl: String,
    createUrl: String,
    sessionOptionsUrl: String,
    sessionUrl: String,
    returnTo: String,
    conditional: { type: Boolean, default: false },
    unsupportedText: String,
    failedText: String,
    workingText: String
  }

  connect() {
    if (!window.PublicKeyCredential) {
      this.buttonTargets.forEach(b => b.classList.add("hidden"))
      return
    }
    this.buttonTargets.forEach(b => b.classList.remove("hidden"))
    if (this.conditionalValue) this.conditional()
  }

  disconnect() {
    this.abort?.abort()
  }

  async register(event) {
    event?.preventDefault()
    this.say(this.workingTextValue)
    try {
      const options = await this.json(this.creationOptionsUrlValue, {})
      const publicKey = this.decodeCreationOptions(options)
      const credential = await navigator.credentials.create({ publicKey })
      const data = await this.json(this.createUrlValue, {
        credential: this.encodeCredential(credential),
        nickname: this.hasNicknameTarget ? this.nicknameTarget.value : ""
      })
      window.location = data.redirect_path || window.location.href
    } catch (error) {
      this.fail(error)
    }
  }

  async signIn(event) {
    event?.preventDefault()
    this.abort?.abort()
    await this.authenticate()
  }

  async conditional() {
    if (!(await PublicKeyCredential.isConditionalMediationAvailable?.())) return
    this.abort = new AbortController()
    await this.authenticate({ mediation: "conditional", signal: this.abort.signal, quiet: true })
  }

  async authenticate({ mediation = undefined, signal = undefined, quiet = false } = {}) {
    try {
      if (!quiet) this.say(this.workingTextValue)
      const options = await this.json(this.sessionOptionsUrlValue, {})
      const publicKey = this.decodeRequestOptions(options)
      const assertion = await navigator.credentials.get({ publicKey, mediation, signal })
      const data = await this.json(this.sessionUrlValue, { credential: this.encodeCredential(assertion), return_to: this.returnToValue })
      window.location = data.redirect_path || "/"
    } catch (error) {
      if (error?.name === "AbortError") return
      if (!quiet) this.fail(error)
    }
  }

  // --- transport -----------------------------------------------------------

  async json(url, body) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token || "" },
      body: JSON.stringify(body)
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`)
    return data
  }

  say(text) {
    if (this.hasMessageTarget) this.messageTarget.textContent = text || ""
  }

  fail(error) {
    const text = error?.message && !/HTTP|NotAllowedError/.test(error.message) ? error.message : this.failedTextValue
    this.say(text)
  }

  // --- base64url <-> ArrayBuffer -------------------------------------------

  decodeCreationOptions(options) {
    return {
      ...options,
      challenge: this.toBuffer(options.challenge),
      user: { ...options.user, id: this.toBuffer(options.user.id) },
      excludeCredentials: (options.excludeCredentials || []).map(c => ({ ...c, id: this.toBuffer(c.id) }))
    }
  }

  decodeRequestOptions(options) {
    return {
      ...options,
      challenge: this.toBuffer(options.challenge),
      allowCredentials: (options.allowCredentials || []).map(c => ({ ...c, id: this.toBuffer(c.id) }))
    }
  }

  encodeCredential(credential) {
    const response = credential.response
    const out = {
      id: credential.id,
      rawId: this.toBase64url(credential.rawId),
      type: credential.type,
      authenticatorAttachment: credential.authenticatorAttachment,
      clientExtensionResults: credential.getClientExtensionResults?.() || {},
      response: { clientDataJSON: this.toBase64url(response.clientDataJSON) }
    }
    if (response.attestationObject) {
      out.response.attestationObject = this.toBase64url(response.attestationObject)
      out.response.transports = response.getTransports?.() || []
    } else {
      out.response.authenticatorData = this.toBase64url(response.authenticatorData)
      out.response.signature = this.toBase64url(response.signature)
      out.response.userHandle = response.userHandle ? this.toBase64url(response.userHandle) : null
    }
    return out
  }

  toBuffer(base64url) {
    const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(base64url.length / 4) * 4, "=")
    return Uint8Array.from(atob(base64), c => c.charCodeAt(0)).buffer
  }

  toBase64url(buffer) {
    const bytes = new Uint8Array(buffer)
    let binary = ""
    bytes.forEach(b => { binary += String.fromCharCode(b) })
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
  }
}
